import { useEffect, useMemo, useState } from 'react';
import QRCode from 'qrcode';
import type { BoardPublicQuestion } from '@livequiz/shared';
import { createSocket } from './socket.js';
import { applyTheme } from './theme.js';
import { makeT } from './i18n.js';
import logoFox from './assets/logo-fox.png';

type Mode = 'join' | 'questions';

export function BoardBeamerView({ code }: { code: string }) {
  const lang = new URLSearchParams(window.location.search).get('lang') === 'en' ? 'en' : 'de';
  const t = useMemo(() => makeT(lang), [lang]);
  useEffect(() => {
    applyTheme('nacht');
  }, []);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const [questions, setQuestions] = useState<BoardPublicQuestion[]>([]);
  const [qr, setQr] = useState('');
  const [mode, setMode] = useState<Mode>('join');

  const joinUrl = `${location.origin}/play/qa/${code}${lang === 'en' ? '?lang=en' : ''}`;

  useEffect(() => {
    QRCode.toDataURL(joinUrl, { width: 480, margin: 1, color: { dark: '#0b1020', light: '#ffffff' } })
      .then(setQr)
      .catch(() => setQr(''));
  }, [joinUrl]);

  useEffect(() => {
    const socket = createSocket();
    socket.on('connect', () => {
      setConnected(true);
      socket.emit('qaboard:join', { code, voterToken: 'beamer' }, (r) => {
        if (r.ok) {
          setTitle(r.title);
          setQuestions(r.questions);
        } else setError(r.message);
      });
    });
    socket.on('disconnect', () => setConnected(false));
    socket.on('qaboard:update', ({ questions: q }) => setQuestions(q));
    return () => {
      socket.disconnect();
    };
  }, [code]);

  // Tastatur: Leertaste schaltet zwischen Beitritt (QR) und Fragen um.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === ' ') {
        e.preventDefault();
        setMode((m) => (m === 'join' ? 'questions' : 'join'));
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  if (error) {
    return <div className="h-screen flex items-center justify-center text-3xl text-red-300">{error}</div>;
  }

  const answered = questions.filter((q) => q.status === 'answered').length;

  const questionList = (big: boolean) => (
    <div className={big ? 'grid grid-cols-2 gap-4 content-start' : 'space-y-3'}>
      {questions.slice(0, big ? 12 : 9).map((q) => (
        <div key={q.id} className={`flex items-start gap-4 rounded-2xl bg-white/10 px-6 py-4 ${big ? 'text-3xl break-inside-avoid' : 'text-3xl'}`}>
          <span className="shrink-0 rounded-xl bg-indigo-500 px-4 py-2 text-2xl font-bold">▲ {q.votes}</span>
          <div className="flex-1">
            <div>{q.text}</div>
            {q.status === 'answered' && q.answerText && (
              <div className="mt-1 text-2xl text-green-300">✓ {q.answerText}</div>
            )}
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="h-screen flex flex-col p-8">
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="flex items-center gap-2.5 mb-2">
            <img src={logoFox} alt="quIZz" width={40} height={40} draggable={false} />
            <span className="text-2xl font-extrabold leading-none">qu<span style={{ color: 'var(--accent, #3b82f6)' }}>IZ</span>z</span>
          </div>
          <div className="text-2xl text-indigo-300 uppercase tracking-wide">
            {mode === 'join' ? t('boardSubmit') : t('boardYours')}
          </div>
          <h1 className="text-6xl font-black leading-tight">{title || 'Q&A'}</h1>
        </div>
        <div className="flex items-center gap-2 text-slate-400 text-lg">
          <span className={`w-3 h-3 rounded-full ${connected ? 'bg-green-400' : 'bg-red-400'}`} />
          {t('boardQuestionsCount', { n: questions.length })}{answered > 0 ? t('boardAnsweredSuffix', { n: answered }) : ''}
        </div>
      </div>

      <div className="flex-1 overflow-hidden">
        {mode === 'join' ? (
          <div className="h-full flex gap-10 overflow-hidden">
            {/* Beitritt: QR + Code + Link zum Abspeichern */}
            <div className="shrink-0 w-[38%] min-h-0 overflow-hidden flex flex-col items-center justify-center gap-4 bg-white/5 rounded-3xl p-6">
              {qr && <img src={qr} alt="QR" className="rounded-2xl bg-white p-3 shrink" style={{ width: 'min(100%, 38vh)', height: 'auto', aspectRatio: '1' }} />}
              <div className="text-center">
                <div className="text-2xl text-slate-300">{t('boardCode')}</div>
                <div className="text-6xl font-black tracking-widest leading-tight">{code}</div>
              </div>
              <div className="text-xl text-slate-400 text-center break-all">{joinUrl.replace(/^https?:\/\//, '')}</div>
              <div className="text-lg text-slate-500 text-center">{t('boardScanSave')}</div>
            </div>
            {/* Fragen-Vorschau daneben */}
            <div className="flex-1 overflow-hidden">
              {questions.length === 0 ? (
                <div className="h-full flex items-center justify-center text-3xl text-slate-500">{t('boardEmptyFirst')}</div>
              ) : (
                questionList(false)
              )}
            </div>
          </div>
        ) : (
          <div className="h-full overflow-hidden">
            {questions.length === 0 ? (
              <div className="h-full flex items-center justify-center text-3xl text-slate-500">{t('boardEmpty')}</div>
            ) : (
              questionList(true)
            )}
          </div>
        )}
      </div>

      {/* Umschalter (dezent unten) */}
      <div className="shrink-0 pt-4 flex justify-center">
        <button
          onClick={() => setMode((m) => (m === 'join' ? 'questions' : 'join'))}
          className="rounded-lg bg-white/10 hover:bg-white/20 px-4 py-2 text-slate-300"
        >
          {mode === 'join' ? t('boardHideQr') : t('boardShowQr')}
          <span className="ml-2 text-slate-500">{t('boardSpaceKey')}</span>
        </button>
      </div>
    </div>
  );
}
